import {
	Card,
	CardBody,
	Input,
	TableRow,
	Table,
	TableCell,
	TableContainer,
	TableHeader,
	Button,
} from '@windmill/react-ui';
import ExportData from 'components/common/ExportData';
import SeoPageTable from 'components/seoManageCity/SeoPageTable';
import DataWrapper from './DataWrapper';
import NotFound from 'components/table/NotFound';
import PageTitle from 'components/Typography/PageTitle';
import { SidebarContext } from 'context/SidebarContext';
import useAsync from 'hooks/useAsync';
import useFilter from 'hooks/useFilter';
import { useState, useContext, useEffect } from 'react';
import { FiPlus } from 'react-icons/fi';
import SeoPageDrawer from 'components/drawer/SeoPageDrawer';
import MainDrawer from 'components/drawer/MainDrawer';
import DeleteModal from 'components/modal/DeleteModal';
import useToggleDrawer from 'hooks/useToggleDrawer';
import SeoPageServices from 'services/SeoPageServices';
import sidebar from 'routes/sidebar';
import { FiRefreshCcw } from 'react-icons/fi';

const SeoManageCity = () => {
	const adminInfo = localStorage.getItem('adminInfo')
		? JSON.parse(localStorage.getItem('adminInfo'))
		: null;
	const { toggleDrawer, setIsUpdate } = useContext(SidebarContext);
	const { title, serviceId } = useToggleDrawer();
	const [offset, setOffset] = useState(0);
	const [data, setData] = useState([]);
	const [status, setStatus] = useState('');
	const [originalData, setOriginalData] = useState([]);
	// Fetch data when offset changes
	const { loading, setLoading, error } = useAsync(() =>
		SeoPageServices.getAllSeoPage(offset)
	);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const response = await SeoPageServices.getAllSeoPage(offset);
				if (response) {
					setLoading(false);
					setStatus(response.status);
					setData((prevData) => [...prevData, ...response.data]);
					setOriginalData((prevData) => [...prevData, ...response.data]);
				}
			} catch (error) {
				setLoading(false);
				console.error('Error fetching data:', error);
			}
		};

		fetchData();
		//eslint-disable-next-line
	}, [offset]);

	const [itemsPerPage, setItemsPerPage] = useState(10);
	const { seoPageRef, serviceData } = useFilter(
		Array.isArray(data?.data) ? data?.data : [],
		itemsPerPage
	);
	const updatedData = data?.data?.map((item) => {
		const { id, ...rest } = item; // Use destructuring to remove id
		return rest; // Return the object without id
	});

	useEffect(() => {
		if (serviceData.length !== 0) {
			setItemsPerPage(serviceData.length);
		}
	}, [serviceData]);

	const isAdmin = adminInfo?.id === 1;

	const hideView = isAdmin
		? true
		: adminInfo?.permission?.some(
				(perm) => perm.id_tab === 'seo page' && perm.view === 1
		  );

	const hideDelete = isAdmin
		? true
		: adminInfo?.permission?.some(
				(perm) => perm.id_tab === 'seo page' && perm.delete === 1
		  );

	const hideEdit = isAdmin
		? true
		: adminInfo?.permission?.some(
				(perm) => perm.id_tab === 'seo page' && perm.edit === 1
		  );

	const hideAdd = isAdmin
		? true
		: adminInfo?.permission?.some(
				(perm) => perm.id_tab === 'seo page' && perm.add === 1
		  );

	const hideExport = isAdmin
		? true
		: adminInfo?.permission?.some(
				(perm) => perm.id_tab === 'seo page' && perm.export === 1
		  );

	const handleSearch = () => {
		const searchTerms = seoPageRef.current.value
			?.toLowerCase()
			.trim()
			.split(' ')
			.filter(Boolean);

		if (!searchTerms.length) {
			setData(originalData); // Reset to original data if search is empty
			return;
		}

		const filteredData = originalData.filter((search) =>
			searchTerms.every(
				(term) =>
					search?.url?.toLowerCase().includes(term) ||
					search?.keywords?.toLowerCase().includes(term) ||
					search?.title?.toLowerCase().includes(term)
			)
		);

		setData(filteredData);
	};

	const handleRefresh = () => {
		setIsUpdate(true);
	};

	return (
		<>
			{hideView && (
				<>
					<DeleteModal id={serviceId} title={title} />
					<MainDrawer>
						<SeoPageDrawer id={serviceId} />
					</MainDrawer>
					<PageTitle sidebar={sidebar().sidebar} />
					<Card className="min-w-0 shadow-xs overflow-hidden bg-white dark:bg-gray-800 mb-5">
						<CardBody>
							<div className="flex gap-6 items-center">
								<div className="flex-grow-0 md:flex-grow lg:flex-grow xl:flex-grow">
									<Input
										ref={seoPageRef}
										className="border h-10 text-sm focus:outline-none block w-full bg-gray-100 border-transparent focus:bg-white"
										type="search"
										name="search"
										autoComplete="off"
										placeholder="Quick Search"
										onChange={handleSearch}
									/>
								</div>
								{hideAdd && (
									<Button
										onClick={toggleDrawer}
										className="w-56 rounded-md h-10"
									>
										<span className="mr-3">
											<FiPlus />
										</span>
										Add Seo
									</Button>
								)}
								{hideExport && (
									<div className="items-center">
										<ExportData exportData={updatedData} fileNames="Seo Page" />
									</div>
								)}

								<FiRefreshCcw
									onClick={() => {
										handleRefresh();
									}}
									cursor="pointer"
								/>
							</div>
						</CardBody>
					</Card>
					<DataWrapper loading={loading} error={error} status={status}>
						{data?.length !== 0 ? (
							<TableContainer>
								<div
									className="overflow-y-scroll"
									style={{ maxHeight: '65vh', scrollbarWidth: 'thin' }}
								>
									<Table>
										<TableHeader className="custom-table-header capitalize text-center sticky top-0 z-10 text-sm">
											<TableRow>
												<TableCell>Sl.No.</TableCell>
												<TableCell>URL</TableCell>
												<TableCell>Keywords</TableCell>
												<TableCell>Title</TableCell>
												<TableCell>Actions</TableCell>
											</TableRow>
										</TableHeader>
										<SeoPageTable
											hourRentalBasicRate={data}
											perpage={data.length}
											currentPage={data.length}
											hideEdit={hideEdit}
											hideDelete={hideDelete}
											setOffset={setOffset}
										/>
									</Table>
								</div>
							</TableContainer>
						) : (
							<NotFound title="There are no Pages." />
						)}
					</DataWrapper>
				</>
			)}
		</>
	);
};

export default SeoManageCity;
